import { apiClient } from '@/lib/api/client'

// ─── Types ──────────────────────────────────────────────
//
// Mirrors the kg_* tables (migration 202610170001). A concept is addressed by its UCID, which is
// stable across nightly graph builds; `name` is the curator's display name when set, else the
// build's canonical name (`buildName`).

export type ConceptSource = 'legacy_2024' | 'enrichment_v2'
export type ConceptSort = 'artworks' | 'degree' | 'members' | 'name' | 'specificity'

// The categories a curator can assign (lib/knowledge-graph/categorize.js CATEGORIES).
export const CATEGORIES = [
  'style', 'movement', 'technique', 'composition', 'color', 'mood', 'theme', 'subject', 'object',
  'figure', 'place', 'symbolism', 'narrative', 'influence', 'experience', 'other',
] as const

export interface Concept {
  ucid: string
  name: string
  buildName: string
  displayName: string | null
  /** The curator's category when set, else the build's. */
  category: string | null
  buildCategory: string | null
  curatorCategory: string | null
  /** 0..1: how much this concept says about an artwork (1 = on one artwork, ~0 = on all of them). */
  specificity: number | null
  /** The curator's override of specificity, or null for automatic. */
  curatorWeight: number | null
  /** What search ranks by: curatorWeight ?? specificity. */
  weight: number | null
  suggestedCategory: string | null
  suggestedConfidence: number | null
  conceptTypes: string[]
  legacyFacets: string[]
  memberNames: string[]
  sources: ConceptSource[]
  resolutionType: string | null
  artworkCount: number
  degree: number
  active: boolean
  hidden: boolean
  notes: string | null
  firstSeenRun: string
  lastSeenRun: string
}

export interface RelatedConcept extends Concept {
  edgeType?: string
  similarity?: number | null
  shared?: number
}

export interface ConceptArtwork {
  id: number
  name: string
  fileName: string | null
  status: string
  artistName: string | null
  edgeType: string
  strength: number | null
  provenance: ConceptSource[]
}

// ─── Model proposals (semantic entity resolution) ─────

export type ProposalAction = 'merge' | 'split' | 'link' | 'category' | 'generic' | 'reject'
export type RelationType = 'broader_than' | 'variant_of' | 'opposite_of' | 'related_to'
export const RELATION_TYPES: RelationType[] = ['broader_than', 'variant_of', 'opposite_of', 'related_to']

export interface ProposalConcept {
  ucid: string
  name: string
  category: string | null
  memberNames: string[]
  sources: ConceptSource[]
  artworkCount: number
  weight: number | null
  active: boolean
  artworkIds: number[]
  missing?: boolean
}

/** Payload keys arrive camelCased by the API. */
export interface ProposalPayload {
  sourceEvidence?: Array<{ id: string; name: string; sourceUri: string; scope: string | null; targetId: string | null; semanticEntailment: string | null; claimRelation?: string | null; polarity?: string | null; alignmentStatus: string }>
  operation?: 'add' | 'replace' | 'remove'
  previousRelation?: { sourceUcid: string; targetUcid: string; relationType: RelationType }
  graphRunId?: string
  evidence?: string
  canonicalName?: string
  keepUcid?: string
  splitNames?: string[]
  keepNames?: string[]
  relationType?: RelationType
  sourceName?: string
  targetName?: string
  category?: string
  previous?: string | null
  suggestedWeight?: number
  rejectionNote?: string
}

export interface Proposal {
  proposalId: string
  runId: string
  task: string
  action: ProposalAction
  ucids: string[]
  payload: ProposalPayload
  confidence: number | null
  reasoning: string | null
  model: string | null
  proposedAt: string
  status: 'pending' | 'accepted' | 'rejected' | 'stale'
  concepts: ProposalConcept[]
}

export interface SerRun {
  runId: string
  startedAt: string | null
  finishedAt: string | null
  model: string | null
  tasks: string | null
  budgetUsd: number | null
  spentUsd: number | null
  calibrationCatch: number | null
  calibrationPassed: boolean | null
  candidates: number | null
  verdicts: number | null
  proposals: number | null
  status: string | null
  notes: string | null
}

/** What accepting every pending proposal of an action at or above a confidence would do. */
export interface ThresholdPreview {
  action: ProposalAction
  minConfidence: number
  eligible: number
  /** Held back: they contradict another pending proposal (e.g. merge and remove the same concept). */
  held: number
  artworkLinks: number
  /** Pending proposals of this action by the model's stated confidence. */
  bands: Array<{ band: number; n: number }>
}

/** A background accept by confidence (Cloud Tasks); the admin polls these while one is open. */
export interface BulkJob {
  id: number
  action: ProposalAction
  minConfidence: number
  total: number
  accepted: number
  failed: Array<{ id: string; message: string }>
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled'
  /** 'autopilot' when a policy started it, 'manual' when a person did. */
  origin: 'manual' | 'autopilot'
  error: string | null
  createdAt: string
  updatedAt: string
  finishedAt: string | null
}

export interface ProposalSummary {
  pending: Array<{ action: ProposalAction; n: number }>
  byStatus: Record<string, number>
  latestRun: SerRun | null
}

export interface ConceptRelation {
  sourceUcid: string
  targetUcid: string
  relationType: RelationType
  origin: 'model' | 'curator'
  direction: 'out' | 'in'
  ucid: string
  name: string
  category: string | null
  artworkCount: number
}

export interface AcceptEdits {
  canonicalName?: string | null
  category?: string
  splitNames?: string[]
  relationType?: RelationType
  weight?: number
}

export type DecisionAction =
  | 'rename' | 'hide' | 'unhide' | 'merge' | 'separate' | 'reject' | 'note' | 'category' | 'weight' | 'dismiss'
  | 'link' | 'distinct'

/** What the build did with a rule (Picasso's art_staging.curation_applied). */
export type AppliedStatus = 'applied' | 'already' | 'no_match' | 'conflict'

export interface CurationDecision {
  id: number
  action: DecisionAction
  ucids: string[]
  payload: Record<string, unknown>
  decidedAt: string
  appliedRunId: string | null
  appliedStatus: AppliedStatus | null
  appliedEffect: string | null
  revokedAt: string | null
  exportedAt?: string | null
  decidedByEmail: string | null
}

export interface ConceptDetail {
  concept: Concept
  /** The UCID that was asked for, when it was retired by a merge and redirected here. */
  redirectedFrom: string | null
  artworks: ConceptArtwork[]
  neighbors: RelatedConcept[]
  coOccurring: RelatedConcept[]
  decisions: CurationDecision[]
  /** Open merge / separate / reject rules touching this concept. */
  rules: CurationDecision[]
  /** Typed relations (broader / variant / opposite / related), both directions. */
  relations: ConceptRelation[]
  /** Pending model proposals that involve this concept. */
  proposals: Proposal[]
}

export interface BuildRun {
  runId: string
  syncedAt: string
  concepts: number
  artworkLinks: number
  edges: number
  redirects: number
  unmatchedArtworks: number
  durationMs: number | null
  newConcepts: number | null
  retiredConcepts: number | null
  mergedConcepts: number | null
  rulesApplied: number | null
}

// ─── Review queue ──────────────────────────────────────

export type ReviewKind = 'generic' | 'suspect_merge' | 'duplicate' | 'uncategorised' | 'thin'

export interface ReviewConcept {
  ucid: string
  name: string
  category: string | null
  memberNames: string[]
  sources: ConceptSource[]
  artworkCount: number
  specificity?: number | null
  weight?: number | null
  suggestedCategory?: string | null
  suggestedConfidence?: number | null
}

export interface ReviewItem {
  kind: ReviewKind
  score: number
  detail: {
    artworkCount?: number
    share?: number
    specificity?: number | null
    suggested?: string | null
    confidence?: number | null
    similarity?: number
    reason?: string
    sharedArtworks?: number
    opposites?: Array<{ a: string; b: string; words: [string, string] }>
    unrelated?: string | null
    overlap?: number
    members?: number
  }
  concept: ReviewConcept
  other: ReviewConcept | null
}

export interface ReviewSummary {
  counts: Record<ReviewKind, number>
  computedAt: string | null
}

// ─── Progress (how far the graph is formalized) ─────────

/** Keys arrive camelCased (relation types and review kinds included). */
export interface HealthMetrics {
  concepts: number
  artworkLinks: number
  multiNameConcepts: number
  categorisedConcepts: number
  categorisedLinkShare: number
  reviewedConcepts: number
  reviewedLinkShare: number
  weightedConcepts: number
  downweightedLinkShare: number
  hiddenConcepts: number
  mergedIds: number
  rules: Record<string, { open: number; applied: number }>
  removalPending: { concepts: number; links: number }
  relations: Record<string, number>
  relationsTotal: number
  proposals: Record<string, { pending?: number; accepted?: number; rejected?: number; stale?: number }>
  review: Record<string, number>
  ser: { runs: number; spent: number; verdicts: number; proposals: number }
}

export interface HealthSnapshot {
  takenAt: string
  source: string
  runId: string | null
  metrics: HealthMetrics
}

export interface KnowledgeHealth {
  now: HealthMetrics
  acceptance: Record<string, { decided: number; accepted: number; rate: number | null; pending: number }>
  history: HealthSnapshot[]
  runs: SerRun[]
}

// ─── Resolution agent & autopilot ──────────────────────

/** Actions autopilot can accept unseen. Relations and splits are always reviewed one by one. */
export type PolicyAction = 'category' | 'generic' | 'merge' | 'reject'

export interface AutopilotPolicy {
  action: PolicyAction
  enabled: boolean
  minConfidence: number
  updatedAt: string
  /** Pending proposals of this action, and how many the policy would accept / hold back now. */
  pending: number
  eligible: number
  held: number
  artworkLinks: number
}

/** How proposals were decided: by a person one by one, in a person's bulk accept, by autopilot, or rejected. */
export interface ActionOutcomes {
  action: ProposalAction
  pending: number
  accepted: number
  rejected: number
  stale: number
  person: number
  bulk: number
  autopilot: number
  /** One-by-one accepts + rejects: what a person actually looked at. */
  reviewed: number
  /** Of those, the share accepted — the model's precision as people judge it. */
  precision: number | null
}

export interface AgentDay {
  day: string
  proposed: number
  person: number
  bulk: number
  autopilot: number
  rejected: number
}

export interface AgentStats {
  totals: {
    runs: number
    workingRuns: number
    calibratedRuns: number
    spentUsd: number
    verdicts: number
    proposals: number
    candidates: number
    inputTokens: number
    outputTokens: number
    lastFinishedAt: string | null
    spentUsdWindow: number
    verdictsWindow: number
    costPerVerdict: number | null
    calibrationPassRate: number | null
    medianDecisionHours: number | null
  }
  runs: SerRun[]
  byAction: ActionOutcomes[]
  policies: AutopilotPolicy[]
  /** What is waiting, by what would happen to it. */
  coverage: { eligible: number; eligibleIfEnabled: number; held: number; below: number; manualOnly: number }
  bands: Array<{ action: ProposalAction; band: number; n: number }>
  daily: AgentDay[]
  jobs: BulkJob[]
  days: number
}

export interface AutopilotStart {
  action: PolicyAction
  jobId?: number
  total?: number
  skipped?: string
}

export interface PipelineStage {
  state: string
  message?: string | null
  execution?: string
  startedAt?: string
  finishedAt?: string | null
  lastAttemptAt?: string | null
  errorCode?: number
  schedule?: string
  timeZone?: string
}
export interface ResolutionExample {
  proposalId: string
  action: ProposalAction
  ucids: string[]
  payload: ProposalPayload & { names?: Record<string, string> }
  decisionPayload: {
    from?: string | number | null
    to?: string | number | null
    conceptNames?: string[]
    conceptName?: string
    groups?: string[][]
  }
  reasoning: string
  confidence: number
  decidedAt: string
  origin: string
  appliedStatus: AppliedStatus | null
  appliedRunId: string | null
  exportedAt: string | null
  revokedAt: string | null
  conceptNames: string[]
  artworkIds: number[]
}
export interface GardenOperations {
  observedAt: string
  days: number
  timeZone: string
  pipeline: {
    build: PipelineStage
    sync: PipelineStage
    proposals: PipelineStage
    export: PipelineStage
  }
  lastBuild: BuildRun | null
  lastProposalPullAt: string | null
  resolution: (SerRun & { observedState: string }) | null
  totals: {
    pending: number
    acceptedWindow: number
    autopilotWindow: number
    applications: { applied: number; waiting: number; needsAttention: number }
  }
  counts: Array<{
    action: ProposalAction
    status: string
    n: number
    decidedWindow: number
    autopilotWindow: number
  }>
  daily: Array<{ day: string; proposed: number; autopilot: number; person: number }>
  examples: ResolutionExample[]
  jobs: BulkJob[]
  policies: AutopilotPolicy[]
}

export interface GraphOverview {
  categories: Array<{ category: string; concepts: number; artworkLinks: number; merged: number }>
  top: Array<{ ucid: string; name: string; category: string; artworkCount: number; degree: number; rank: number }>
  /** Concept-to-concept edges between two categories (a <= b; a === b is within one category). */
  affinity: Array<{ a: string; b: string; edges: number }>
  relations: Array<{ relationType: RelationType; origin: 'model' | 'curator'; n: number }>
}

// ─── Search preview ────────────────────────────────────

export interface PreviewConcept {
  ucid: string
  name: string
  category: string | null
  artworkCount: number
  specificity: number | null
  weight: number | null
  hidden: boolean
  rejected: boolean
  similarity: number
  effectiveWeight: number
  excluded: boolean
  used: boolean
  contribution: number
}

export interface PreviewArtwork {
  id: number
  name: string
  fileName: string | null
  status: string
  artistName: string | null
  score: number
  reasons: string[]
}

export interface SearchPreview {
  query: string
  mode: 'curated' | 'raw'
  floor: number
  concepts: PreviewConcept[]
  artworks: PreviewArtwork[]
  judgments?: Array<{ artworkId: number; verdict: 1 | -1 }>
}

export interface SavedQuery {
  id: number
  query: string
  notes: string | null
  good: number
  bad: number
  updatedAt: string
}

export interface EvaluationRow {
  id: number
  query: string
  good: number
  bad: number
  curated: { goodFound: number; goodRecall: number | null; badInTop: number }
  raw: { goodFound: number; goodRecall: number | null; badInTop: number }
}

// ─── Explore ───────────────────────────────────────────

export interface WeightedConcept {
  ucid: string
  name: string
  category: string | null
  artworkCount: number
  artworks?: number
  score?: number
  weight?: number
}

export interface ArtistFingerprint {
  artist: { id: number; name: string; artworks: number; artworksInGraph: number }
  concepts: WeightedConcept[]
  categories: Array<{ category: string; score: number }>
  similar: Array<{ id: number; name: string; shared: number; similarity: number | null }>
}

export interface ConceptPath {
  from: string
  to: string
  sharedArtworks: number
  steps: Array<WeightedConcept & { similarity: number | null }> | null
  fromConcept: WeightedConcept | null
  toConcept: WeightedConcept | null
}

export interface ArtworkSummary {
  id: number
  name: string
  fileName: string | null
  status: string
  artistName: string | null
}

export interface ArtworkComparison {
  a: ArtworkSummary
  b: ArtworkSummary
  shared: WeightedConcept[]
  onlyA: WeightedConcept[]
  onlyB: WeightedConcept[]
  overlap: number
}

export interface RunChange {
  kind: 'new' | 'retired' | 'merged'
  ucid: string
  name: string
  detail: { artworkCount?: number; into?: string; intoName?: string }
}

export interface RunChanges {
  run: BuildRun
  new: RunChange[]
  retired: RunChange[]
  merged: RunChange[]
  applied: CurationDecision[]
}

export interface KnowledgeStats {
  run: BuildRun | null
  concepts: number
  mergedConcepts: number
  hidden: number
  artworkLinks: number
  artworks: number
  edges: number
  categories: Array<{ category: string; n: number }>
  sources: Array<{ source: ConceptSource; n: number }>
}

export interface ConceptListParams {
  q?: string
  category?: string
  source?: ConceptSource
  sort?: ConceptSort
  includeHidden?: boolean
  limit?: number
  offset?: number
}

export interface GraphNode {
  id: string
  kind: 'concept' | 'artwork'
  role?: 'query' | 'matched' | 'direction' | 'facet' | 'lane'
  seed?: boolean
  imageUrl?: string
  name: string
  ucid?: string
  category?: string | null
  artworkCount?: number
  centre?: boolean
  artworkId?: number
  artistName?: string | null
}

export interface GraphEdge {
  source: string
  target: string
  kind: string
  weight: number | null
  basis?: 'similarity' | 'ontology' | 'artwork' | 'search'
  origin?: string
}

export interface ConceptGraph {
  centre: string
  nodes: GraphNode[]
  edges: GraphEdge[]
}

export interface CuratePayload {
  displayName?: string | null
  hidden?: boolean
  notes?: string | null
  category?: string | null
  fromSuggestion?: boolean
  weight?: number | null
}

const BASE = '/api/knowledge-graph'

export interface KnowledgeDomain {
  id: string
  label: string
  labelStatus: string
  state: string
  priority: number
  priorityReasons: string[]
  nextAction: string
  lastReviewedAt: string | null
  pendingReviewConcepts: number
  resolutions?: number[]
  evidence?: Array<{ id: string; name: string; sourceUri: string; scope: string | null; semanticEntailment: string | null; targetId?: string | null; claimRelation?: string | null; polarity?: string | null }>
  representatives: Array<{ ucid: string; name: string; category: string | null }>
  metrics: {
    concepts: number; boundaryConcepts: number; overlapConcepts: number
    cohesion: number; density: number; directArtworkReach: number | null
    typedConceptShare: number; semanticPrecision: number | null
  }
  history: Array<{ runId: string; at: string; status: string; reviewed: number; proposed: number; semanticValidation: string; allocatedCostUsd?: number; samples?: Array<{ ucids: string[]; names: string[]; decision: string; relationType: string | null; confidence: number | null; reason: string; heldReason?: string | null }> }>
}
export interface DomainAgenda {
  available: boolean
  message?: string
  observedAt?: string
  graphRunId?: string
  activeDomainIds?: string[]
  summary?: { domains: number; overlappingConcepts: number; unassignedConcepts: number; semanticValidation: string }
  total?: number
  data: KnowledgeDomain[]
  overlaps?: Array<{ source: string; target: string; shared: number }>
}

export const knowledgeApi = {
  getStats: async (): Promise<KnowledgeStats> => (await apiClient.get(`${BASE}/stats`)).data,

  getConcepts: async (params: ConceptListParams): Promise<{ data: Concept[]; total: number }> =>
    (await apiClient.get(`${BASE}/concepts`, { params })).data,

  getConcept: async (ucid: string): Promise<ConceptDetail> =>
    (await apiClient.get(`${BASE}/concepts/${ucid}`)).data,

  getConceptArtworks: async (
    ucid: string,
    params: { limit?: number; offset?: number }
  ): Promise<{ data: ConceptArtwork[]; total: number }> =>
    (await apiClient.get(`${BASE}/concepts/${ucid}/artworks`, { params })).data,

  getGraph: async (ucid: string, params: { concepts?: number; artworks?: number }): Promise<ConceptGraph> =>
    (await apiClient.get(`${BASE}/concepts/${ucid}/graph`, { params })).data,

  curate: async (ucid: string, payload: CuratePayload): Promise<Concept> =>
    (await apiClient.patch(`${BASE}/concepts/${ucid}`, payload)).data,

  getArtworkConcepts: async (artworkId: number): Promise<{ data: RelatedConcept[] }> =>
    (await apiClient.get(`${BASE}/artworks/${artworkId}/concepts`)).data,

  curateMany: async (items: Array<{ ucid: string; change: CuratePayload }>): Promise<{ data: Concept[] }> =>
    (await apiClient.post(`${BASE}/curate`, { items })).data,

  // Rules the next build applies
  merge: async (ucid: string, ucids: string[]): Promise<CurationDecision> =>
    (await apiClient.post(`${BASE}/concepts/${ucid}/merge`, { ucids })).data,
  separate: async (ucid: string, names: string[]): Promise<CurationDecision> =>
    (await apiClient.post(`${BASE}/concepts/${ucid}/separate`, { names })).data,
  reject: async (ucid: string, reason?: string): Promise<CurationDecision> =>
    (await apiClient.post(`${BASE}/concepts/${ucid}/reject`, { reason })).data,
  getRules: async (includeRevoked = false): Promise<{ data: CurationDecision[]; lastExportAt: string | null }> =>
    (await apiClient.get(`${BASE}/rules`, { params: { includeRevoked } })).data,
  revokeRule: async (id: number): Promise<CurationDecision> => (await apiClient.delete(`${BASE}/rules/${id}`)).data,
  exportRules: async (): Promise<{ rules: number; structural: number; settings: number; exportedAt: string }> =>
    (await apiClient.post(`${BASE}/rules/export`)).data,

  // Model proposals
  getProposalSummary: async (): Promise<ProposalSummary> => (await apiClient.get(`${BASE}/proposals`)).data,
  getProposals: async (params: { action?: ProposalAction; status?: string; limit?: number; offset?: number; domainId?: string }): Promise<{ data: Proposal[]; total: number }> =>
    (await apiClient.get(`${BASE}/proposals/items`, { params })).data,
  acceptProposal: async (id: string, edits: AcceptEdits = {}): Promise<Proposal> =>
    (await apiClient.post(`${BASE}/proposals/${encodeURIComponent(id)}/accept`, edits)).data,
  rejectProposal: async (id: string, note?: string): Promise<Proposal> =>
    (await apiClient.post(`${BASE}/proposals/${encodeURIComponent(id)}/reject`, { note })).data,
  acceptProposals: async (ids: string[]): Promise<{ accepted: number; failed: Array<{ id: string; message: string }> }> =>
    (await apiClient.post(`${BASE}/proposals/accept`, { ids })).data,
  getProposalThreshold: async (action: ProposalAction, minConfidence: number): Promise<ThresholdPreview> =>
    (await apiClient.get(`${BASE}/proposals/threshold`, { params: { action, minConfidence } })).data,
  getBulkJobs: async (): Promise<{ data: BulkJob[] }> => (await apiClient.get(`${BASE}/proposals/jobs`)).data,
  startBulkJob: async (body: { action: ProposalAction; minConfidence: number }): Promise<BulkJob> =>
    (await apiClient.post(`${BASE}/proposals/jobs`, body)).data,
  cancelBulkJob: async (id: number): Promise<BulkJob> => (await apiClient.post(`${BASE}/proposals/jobs/${id}/cancel`)).data,
  syncProposals: async (): Promise<{ runs: number; inserted: number; stale: number }> =>
    (await apiClient.post(`${BASE}/proposals/sync`)).data,
  getDomainAgenda: async (search = '', offset = 0): Promise<DomainAgenda> =>
    (await apiClient.get(`${BASE}/domains`, { params: { search, offset, limit: 20 } })).data,
  getOperations: async (days = 7): Promise<GardenOperations> => (await apiClient.get(`${BASE}/operations`, { params: { days } })).data,

  getAgentStats: async (days = 30): Promise<AgentStats> => (await apiClient.get(`${BASE}/agent`, { params: { days } })).data,
  setAutopilotPolicy: async (action: PolicyAction, change: { enabled?: boolean; minConfidence?: number }): Promise<{ policy: AutopilotPolicy; started: AutopilotStart[] }> =>
    (await apiClient.patch(`${BASE}/autopilot/${action}`, change)).data,
  runAutopilot: async (all: boolean): Promise<{ started: AutopilotStart[] }> =>
    (await apiClient.post(`${BASE}/autopilot/run`, { all })).data,
  getSerRuns: async (): Promise<{ data: SerRun[] }> => (await apiClient.get(`${BASE}/ser-runs`)).data,
  getHealth: async (days = 90): Promise<KnowledgeHealth> => (await apiClient.get(`${BASE}/health`, { params: { days } })).data,
  snapshotHealth: async (): Promise<HealthMetrics> => (await apiClient.post(`${BASE}/health/snapshot`)).data,
  addRelation: async (ucid: string, targetUcid: string, relationType: RelationType): Promise<{ data: ConceptRelation[] }> =>
    (await apiClient.post(`${BASE}/concepts/${ucid}/relations`, { targetUcid, relationType })).data,
  removeRelation: async (sourceUcid: string, targetUcid: string, relationType: RelationType): Promise<void> => {
    await apiClient.delete(`${BASE}/concepts/${sourceUcid}/relations/${targetUcid}/${relationType}`)
  },

  // Review queue
  getReviewSummary: async (): Promise<ReviewSummary> => {
    const r: { counts: Array<{ kind: ReviewKind; n: number }>; computedAt: string | null } = (await apiClient.get(`${BASE}/review`)).data
    const counts = { generic: 0, suspect_merge: 0, duplicate: 0, uncategorised: 0, thin: 0 } as Record<ReviewKind, number>
    for (const c of r.counts) counts[c.kind] = c.n
    return { counts, computedAt: r.computedAt }
  },
  getReviewItems: async (params: { kind: ReviewKind; limit?: number; offset?: number }): Promise<{ data: ReviewItem[]; total: number }> =>
    (await apiClient.get(`${BASE}/review/items`, { params })).data,
  dismiss: async (items: Array<{ kind: ReviewKind; ucid: string; otherUcid?: string }>): Promise<{ dismissed: number }> =>
    (await apiClient.post(`${BASE}/review/dismiss`, { items })).data,
  refreshReview: async (): Promise<Record<string, number>> => (await apiClient.post(`${BASE}/review/refresh`)).data,
  suggestCategories: async (limit = 300): Promise<{ considered: number; suggested: number; remaining: number; errors: string[] }> =>
    (await apiClient.post(`${BASE}/review/suggest-categories`, { limit }, { timeout: 300000 })).data,

  // Search preview
  preview: async (params: { q: string; mode: 'curated' | 'raw'; publishedOnly: boolean; queryId?: number }): Promise<SearchPreview> =>
    (await apiClient.get(`${BASE}/search/preview`, { params })).data,
  getQueries: async (): Promise<{ data: SavedQuery[] }> => (await apiClient.get(`${BASE}/search/queries`)).data,
  saveQuery: async (query: string, notes?: string): Promise<SavedQuery> =>
    (await apiClient.post(`${BASE}/search/queries`, { query, notes })).data,
  deleteQuery: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE}/search/queries/${id}`)
  },
  judge: async (queryId: number, artworkId: number, verdict: 1 | -1 | null): Promise<void> => {
    await apiClient.post(`${BASE}/search/queries/${queryId}/judgments`, { artworkId, verdict })
  },
  evaluate: async (): Promise<{ topK: number; queries: EvaluationRow[] }> =>
    (await apiClient.post(`${BASE}/search/evaluate`, {}, { timeout: 300000 })).data,

  // Explore
  searchArtists: async (q: string): Promise<{ data: Array<{ id: number; name: string; artworksInGraph: number }> }> =>
    (await apiClient.get(`${BASE}/explore/artists`, { params: { q } })).data,
  getArtistFingerprint: async (artistId: number): Promise<ArtistFingerprint> =>
    (await apiClient.get(`${BASE}/explore/artists/${artistId}`)).data,
  getPath: async (from: string, to: string): Promise<ConceptPath> =>
    (await apiClient.get(`${BASE}/explore/path`, { params: { from, to } })).data,
  compareArtworks: async (a: number, b: number): Promise<ArtworkComparison> =>
    (await apiClient.get(`${BASE}/explore/compare`, { params: { a, b } })).data,
  getOverview: async (): Promise<GraphOverview> => (await apiClient.get(`${BASE}/explore/overview`)).data,
  getRuns: async (): Promise<{ data: BuildRun[] }> => (await apiClient.get(`${BASE}/runs`)).data,
  getRunChanges: async (runId: string): Promise<RunChanges> => (await apiClient.get(`${BASE}/runs/${runId}`)).data,
}
